/**
=========================================================
* TripEnhancer - Image Caching Service
=========================================================
*
* Service for fetching and caching images with:
* - Browser-based caching in localStorage
* - Caching to avoid rate limiting
* - Support for multiple image sources (Unsplash, Wikimedia, etc.)
*/

// User-Agent for server-side requests (browsers set this automatically)
// const USER_AGENT = "TripEnhancerApp/1.0 (https://tripenhancer.app; tripenhancerapp@gmail.com)";
const CACHE_KEY_PREFIX = "image_cache_";
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

/**
 * ImageCacheService - Handles all image caching operations
 */
class ImageCacheService {
  constructor() {
    this.cache = new Map();
    this.loadCacheFromStorage();
  }

  /**
   * Load cached images from localStorage
   */
  loadCacheFromStorage() {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(CACHE_KEY_PREFIX)) {
          const data = JSON.parse(localStorage.getItem(key));
          if (data && data.timestamp && Date.now() - data.timestamp < CACHE_DURATION) {
            this.cache.set(key, data);
          } else {
            // Remove expired cache
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      console.error("Failed to load cache from localStorage:", error);
    }
  }

  /**
   * Save data to cache (both memory and localStorage)
   */
  saveToCache(url, data) {
    const cacheKey = CACHE_KEY_PREFIX + this.hashCode(url);
    const cacheData = {
      url: data,
      timestamp: Date.now(),
    };

    this.cache.set(cacheKey, cacheData);

    try {
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.warn("Failed to save to localStorage:", error);
      // If localStorage is full, try to clear old entries
      this.clearOldCache();
      try {
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } catch (e) {
        console.error("Still failed after clearing cache:", e);
      }
    }
  }

  /**
   * Get data from cache
   */
  getFromCache(url) {
    const cacheKey = CACHE_KEY_PREFIX + this.hashCode(url);
    const cached = this.cache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.url;
    }

    return null;
  }

  /**
   * Clear old cache entries
   */
  clearOldCache() {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(CACHE_KEY_PREFIX)) {
          const data = JSON.parse(localStorage.getItem(key));
          if (!data || !data.timestamp || Date.now() - data.timestamp >= CACHE_DURATION) {
            localStorage.removeItem(key);
            this.cache.delete(key);
          }
        }
      });
    } catch (error) {
      console.error("Failed to clear old cache:", error);
    }
  }

  /**
   * Simple hash function for cache keys
   */
  hashCode(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Extract filename from Wikimedia Commons URL
   * @param {string} url - Full Wikimedia Commons URL
   * @returns {string|null} - Filename or null
   */
  extractFilename(url) {
    try {
      const match = url.match(/\/([^/]+)\.(jpg|jpeg|png|gif|svg)(?:\/|\?|$)/i);
      if (match) {
        // Decode URL encoding and get the full filename with extension
        return decodeURIComponent(match[1] + "." + match[2]);
      }
      return null;
    } catch (error) {
      console.error("Failed to extract filename:", error);
      return null;
    }
  }

  /**
   * Convert traditional Wikimedia URL to REST API URL
   * @param {string} url - Traditional Wikimedia Commons URL
   * @returns {string} - REST API URL or original URL if conversion fails
   */
  convertToRestApiUrl(url) {
    // Check if it's already cached
    const cached = this.getFromCache(url);
    if (cached) {
      return cached;
    }

    // If it's not a Wikimedia URL, return as-is
    if (!url.includes("wikimedia.org") && !url.includes("wikipedia.org")) {
      return url;
    }

    try {
      const filename = this.extractFilename(url);
      if (!filename) {
        console.warn("Could not extract filename from URL:", url);
        return url;
      }

      // Extract desired width from original URL
      // let width = 1280; // default
      const widthMatch = url.match(/\/(\d+)px-/);
      if (widthMatch) {
        // width = parseInt(widthMatch[1], 10);
        console.log("Original image width:", widthMatch[1]);
      }

      // Build REST API URL (for reference, not currently used)
      // Format: https://commons.wikimedia.org/w/rest.php/v1/file/File:FILENAME
      // const restApiUrl = `https://commons.wikimedia.org/w/rest.php/v1/file/File:${encodeURIComponent(filename)}`;

      // For now, we'll use the original URL since the REST API requires additional processing
      // The REST API returns JSON with the actual image URL, which requires an async fetch
      // For a synchronous solution, we'll keep using the traditional URLs but mark them as "approved"

      // Cache the URL
      this.saveToCache(url, url);

      return url;
    } catch (error) {
      console.error("Failed to convert URL:", error);
      return url;
    }
  }

  /**
   * Fetch image metadata from Wikimedia REST API
   * @param {string} filename - Image filename (e.g., "Manneken_Pis_2020.jpg")
   * @returns {Promise<Object>} - Image metadata
   */
  async fetchImageMetadata(filename) {
    try {
      const apiUrl = `https://commons.wikimedia.org/w/rest.php/v1/file/File:${encodeURIComponent(
        filename
      )}`;

      // Note: In browser environments, we cannot set custom User-Agent headers
      // due to CORS and browser security restrictions. The User-Agent is automatically
      // set by the browser. For server-side fetching, we would use:
      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Failed to fetch image metadata:", error);
      throw error;
    }
  }

  /**
   * Get optimized image URL with proper caching
   * @param {string} url - Original image URL
   * @param {number} width - Desired width (optional)
   * @returns {string} - Optimized/cached URL
   */
  getOptimizedImageUrl(url, width = 1280) {
    // Check cache first
    const cached = this.getFromCache(url);
    if (cached) {
      return cached;
    }

    // For Unsplash URLs, update size parameters
    if (url.includes("unsplash.com")) {
      const optimizedUrl = url.replace(/w=\d+/, `w=${width}`);
      this.saveToCache(url, optimizedUrl);
      return optimizedUrl;
    }

    // For Wikimedia URLs, ensure we're using the right size
    if (
      (url.includes("wikimedia.org") || url.includes("wikipedia.org")) &&
      url.includes("/thumb/")
    ) {
      // Replace the width in the URL if specified differently
      const optimizedUrl = url.replace(/\/(\d+)px-/, `/${width}px-`);
      this.saveToCache(url, optimizedUrl);
      return optimizedUrl;
    }

    // For other URLs, use as-is
    this.saveToCache(url, url);
    return url;
  }

  /**
   * Preload an image to cache it in the browser
   * @param {string} url - Image URL to preload
   * @returns {Promise<void>}
   */
  async preloadImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(`Failed to preload image: ${url}`));
      img.src = url;
    });
  }

  /**
   * Preload multiple images
   * @param {string[]} urls - Array of image URLs
   * @returns {Promise<void[]>}
   */
  async preloadImages(urls) {
    return Promise.all(
      urls.map((url) =>
        this.preloadImage(url).catch((err) => {
          console.warn("Failed to preload:", url, err);
        })
      )
    );
  }
}

// Create singleton instance
const imageCacheService = new ImageCacheService();

export default imageCacheService;

/**
 * Helper function to get image URL with caching
 * @param {string} url - Original image URL
 * @param {number} width - Desired width (optional)
 * @returns {string} - Optimized URL
 */
export function getImageUrl(url, width) {
  return imageCacheService.getOptimizedImageUrl(url, width);
}

/**
 * Helper function to preload images
 * @param {string|string[]} urls - Image URL(s) to preload
 * @returns {Promise<void>}
 */
export function preloadImages(urls) {
  const urlArray = Array.isArray(urls) ? urls : [urls];
  return imageCacheService.preloadImages(urlArray);
}
